import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { onIdTokenChanged, signOut } from 'firebase/auth';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import TwoFactorVerify from './components/TwoFactorVerify';
import TwoFactorSetup from './components/TwoFactorSetup';
import './app.css';

const TWOFA_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/twofa';
const GIDU_MY_ORGS_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/gidu-my-orgs';

function App() {
  const [user, setUser]         = useState(null);
  const [gido, setGido]         = useState(null);
  // null = not yet asked. Whether this login has an authenticator enrolled.
  const [enrolled, setEnrolled] = useState(null);
  const [quota, setQuota]       = useState(null);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [upgradeNotice, setUpgradeNotice] = useState(null); // 'success' | 'cancelled' | null
  const [openUpgrade, setOpenUpgrade] = useState(false); // deep-link from ?upgrade=1 (email CTA)
  const [sub, setSub] = useState(null);
  const [storageUsedGb, setStorageUsedGb] = useState(0);
  const [fileCount, setFileCount] = useState(0);
    // D-082: one login may reach several organisations. The active org is state,
  // never derived from the uid. Reachability is re-checked server-side on every
  // gidu-my-orgs call, so this is deliberately not persisted in localStorage.
  const [orgs, setOrgs] = useState([]);
  const [activeGido, setActiveGido] = useState(null);
  const [orgsLoaded, setOrgsLoaded] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Referral attribution — store before cleaning URL
    const ref = params.get('ref');
    if (ref && ref.startsWith('REF-')) {
      localStorage.setItem('dbos_referred_by', ref);
    }
    if (params.get('upgrade') === '1') {
      setOpenUpgrade(true);   // email CTA: open the plan modal after auth
    }
    if (params.get('upgraded') === 'true') {
      setUpgradeNotice('success');
    } else if (params.get('upgrade') === 'cancelled') {
      setUpgradeNotice('cancelled');
    }
    if (params.has('upgraded') || params.has('upgrade') ||
        params.has('session_id') || params.has('return_to') || params.has('ref')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const sessionOK = sessionStorage.getItem('twofa_ok') === firebaseUser.uid;
        setVerified(sessionOK);
        if (sessionOK) {
          await loadOrgs();
        } else {
          await checkTwofaStatus();
        }
      } else {
        setUser(null); setGido(null); setQuota(null); setVerified(false);
        setOrgs([]); setActiveGido(null); setOrgsLoaded(false);
        setEnrolled(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Ask the backend which organisations this login can reach, and land on
  // default_org. orgs may legitimately be empty - a login that has registered
  // nothing and accepted no invite.
  const loadOrgs = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(GIDU_MY_ORGS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'list' }),
      });
      const data = await res.json();
      if (!res.ok) { setOrgsLoaded(true); return; }
      setOrgs(data.orgs || []);
      const target = data.default_org || null;
      setActiveGido(target);
      if (target) await loadClientData(target, data.orgs || []);
    } catch (e) {
      console.error('Error loading organisations:', e);
    }
    setOrgsLoaded(true);
  };

  // Does this login have an authenticator enrolled? twofa_secrets is rules-locked
  // (read, write: if false), so only the backend can answer - the browser cannot and
  // should not read it. Asked before choosing setup or verify: a GIDU seat holder who
  // just accepted an invitation has never registered, so has no authenticator, and
  // asking them for a code is a dead end.
  //
  // Fails CLOSED to "enrolled". Guessing "not enrolled" would render the setup screen,
  // whose enroll call OVERWRITES twofa_secrets/{uid} with a fresh secret - killing a
  // working authenticator entry. Asking someone for a code they do have is a far
  // cheaper mistake than silently invalidating it.
  const checkTwofaStatus = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(TWOFA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'status' }),
      });
      const data = await res.json();
      setEnrolled(res.ok ? data.enrolled === true : true);
    } catch (e) {
      console.error('Could not read 2FA status:', e);
      setEnrolled(true);
    }
  };

  // The org named by the switcher, remembered server-side in gidu_prefs.
  const selectOrg = async (gidoId) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(GIDU_MY_ORGS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'select', gido_id: gidoId }),
      });
      if (!res.ok) return;              // 403 not_reachable - leave the current org in place
      setActiveGido(gidoId);
      setQuota(null); setSub(null); setStorageUsedGb(0); setFileCount(0);
      await loadClientData(gidoId, orgs);
    } catch (e) {
      console.error('Error selecting organisation:', e);
    }
  };

  // An owner has a gido_registry record (keyed by uid). A seat or consultant does
  // NOT - for them the org identity comes from the gidu-my-orgs entry itself.
  const loadClientData = async (gidoId, orgList) => {
    try {
      const entry = (orgList || []).find((o) => o.gido_id === gidoId) || null;
      let gidoData = null;

      if (entry?.is_owner) {
        const gidoDoc = await getDoc(doc(db, 'gido_registry', auth.currentUser.uid));
        if (gidoDoc.exists()) gidoData = gidoDoc.data();
      }
      if (!gidoData && entry) {
        gidoData = {
          gido_id: entry.gido_id,
          company_name: entry.name,
          grants: entry.grants || {},
          license_level: entry.license_level,
        };
      }
      if (!gidoData) return;

      gidoData = {
        ...gidoData,
        is_owner: !!entry?.is_owner,
        access_source: entry?.access_source || 'owner',
        expires_at: entry?.expires_at || null,
      };
      setGido(gidoData);

      const quotaDoc = await getDoc(doc(db, 'smartdata_quotas', gidoId));
      if (quotaDoc.exists()) setQuota(quotaDoc.data());
      const subDoc = await getDoc(doc(db, 'subscriptions', gidoId));
      if (subDoc.exists()) setSub(subDoc.data());
      await computeStorage(gidoId);
    } catch (error) {
      console.error('Error loading client data:', error);
    }
  };

  const computeStorage = async (gidoId) => {
    try {
      const filesRef = collection(db, 'smartdata_quotas', gidoId, 'files');
      const snapshot = await getDocs(filesRef);
      let bytes = 0;
      let count = 0;
      snapshot.forEach(d => {
        const f = d.data();
        if (f.status !== 'deleted') {
          bytes += (f.size_bytes || 0);
          count += 1;
        }
      });
      setStorageUsedGb(bytes / (1024 ** 3));
      setFileCount(count);
    } catch (error) {
      console.error('Error computing storage:', error);
    }
  };

  const refreshQuota = async () => {
    if (!gido?.gido_id) return;
    try {
      const quotaDoc = await getDoc(doc(db, 'smartdata_quotas', gido.gido_id));
      if (quotaDoc.exists()) setQuota(quotaDoc.data());
      await computeStorage(gido.gido_id);
    } catch (error) {
      console.error('Error refreshing quota:', error);
    }
  };

  const handleLogout = async () => {
    sessionStorage.removeItem('twofa_ok');
    try {
      const t = await auth.currentUser.getIdToken();
      await fetch(TWOFA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` },
        body: JSON.stringify({ action: 'reset' }),
      });
    } catch (e) {}
    await signOut(auth);
  };
  const handle2faSuccess = async () => {
    sessionStorage.setItem('twofa_ok', auth.currentUser.uid);
    setVerified(true);
    // loadOrgs, not loadClientData: the org has to be discovered before it can be
    // loaded. This call site predates the switcher and still passed a uid as the
    // gido_id with no org list, so `entry` was always null and setGido never ran -
    // every login arriving through the portal's own 2FA gate landed on an empty
    // dashboard. It looked selective only because anyone whose browser already had
    // twofa_ok on this origin took the loadOrgs path instead.
    await loadOrgs();
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">DBOS</div>
        <div className="loading-spinner"></div>
      </div>
    );
  }
  if (!user)     return <Login />;
  if (!verified) {
    if (enrolled === null) {
      return (
        <div className="loading-screen">
          <div className="loading-logo">DBOS</div>
          <div className="loading-spinner"></div>
        </div>
      );
    }
    return enrolled
      ? <TwoFactorVerify onSuccess={handle2faSuccess} onCancel={handleLogout} />
      : <TwoFactorSetup onComplete={handle2faSuccess} onCancel={handleLogout} />;
  }
  return (
    <>
      {upgradeNotice === 'success' && (
        <div className="upgrade-banner success">
          🎉 Payment received — your plan is upgrading now. It may take a minute to reflect everywhere.
          <button onClick={() => setUpgradeNotice(null)}>✕</button>
        </div>
      )}
      {upgradeNotice === 'cancelled' && (
        <div className="upgrade-banner cancelled">
          No charge was made — you're still on your current plan.
          <button onClick={() => setUpgradeNotice(null)}>✕</button>
        </div>
      )}
      <Dashboard
        user={user}
        gido={gido}
        quota={quota}
        sub={sub}
        storageUsedGb={storageUsedGb}
        fileCount={fileCount}
        onLogout={handleLogout}
        onRefresh={() => activeGido && loadClientData(activeGido, orgs)}
        onRefreshQuota={refreshQuota}
        openUpgrade={openUpgrade}
        orgs={orgs}
        activeGido={activeGido}
        onSelectOrg={selectOrg}
      />
    </>
  );
}

export default App;